import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Client, CheckIn, ProgressData, WeeklyCheckIn, GroupTrainingPlan, Exercise, TrainingPlanExercise } from '../types';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Calendar, 
  Phone, 
  Edit, 
  Trash2,
  TrendingUp,
  Activity,
  Target,
  BarChart3,
  FileText,
  Download,
  Plus,
  Eye,
  Clock,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';

// Weight Chart Component
const WeightChart: React.FC<{ weightData: { date: string; weight: number }[] }> = ({ weightData }) => {
  const chartRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    if (!chartRef.current || weightData.length === 0) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Clear previous chart
    ctx.clearRect(0, 0, chartRef.current.width, chartRef.current.height);

    const canvas = chartRef.current;
    const width = canvas.width;
    const height = canvas.height;
    const padding = 40;

    // Calculate scales
    const weights = weightData.map(d => d.weight);
    const minWeight = Math.min(...weights);
    const maxWeight = Math.max(...weights);
    const weightRange = maxWeight - minWeight;
    const yScale = (height - 2 * padding) / (weightRange || 1);
    const xScale = (width - 2 * padding) / (weightData.length - 1);

    // Draw grid lines
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines
    const gridLines = 5;
    for (let i = 0; i <= gridLines; i++) {
      const y = padding + (i * (height - 2 * padding)) / gridLines;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Vertical grid lines
    for (let i = 0; i < weightData.length; i++) {
      const x = padding + i * xScale;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }

    // Draw weight line
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    weightData.forEach((point, index) => {
      const x = padding + index * xScale;
      const y = height - padding - ((point.weight - minWeight) * yScale);
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    ctx.stroke();

    // Draw data points
    ctx.fillStyle = '#3b82f6';
    weightData.forEach((point, index) => {
      const x = padding + index * xScale;
      const y = height - padding - ((point.weight - minWeight) * yScale);
      
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      ctx.fill();
    });

    // Draw labels
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    // Y-axis labels (weight values)
    for (let i = 0; i <= gridLines; i++) {
      const weight = minWeight + (i * weightRange) / gridLines;
      const y = padding + (i * (height - 2 * padding)) / gridLines;
      ctx.fillText(`${weight.toFixed(1)} kg`, padding - 10, y + 4);
    }

    // X-axis labels (dates)
    ctx.textAlign = 'center';
    weightData.forEach((point, index) => {
      const x = padding + index * xScale;
      const date = new Date(point.date);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      ctx.fillText(dateStr, x, height - padding + 20);
    });

  }, [weightData]);

  return (
    <canvas
      ref={chartRef}
      width={600}
      height={256}
      className="w-full h-full"
    />
  );
};

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [latestCheckIn, setLatestCheckIn] = useState<WeeklyCheckIn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'checkins' | 'progress' | 'materials' | 'training-plans'>('overview');
  const [trainingPlans, setTrainingPlans] = useState<GroupTrainingPlan[]>([]);
  const [showTrainingPlanModal, setShowTrainingPlanModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [planExercises, setPlanExercises] = useState<Record<number, TrainingPlanExercise[]>>({});

  const fetchClientData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch client details
      const clientData = await apiService.getClient(parseInt(id));
      setClient(clientData);
      
      // Fetch client check-ins
      const checkInsData = await apiService.getClientCheckIns(parseInt(id));
      setCheckIns(checkInsData);
      
      // Fetch progress data
      const progressData = await apiService.getClientProgress(parseInt(id));
      setProgressData(progressData);
      
      // Fetch latest check-in (only for 1-on-1 clients)
      if (clientData.category !== 'group') {
        try {
          const trainerClientData = await apiService.getTrainerClientCheckIns(parseInt(id));
          if (trainerClientData.checkins && trainerClientData.checkins.length > 0) {
            // Get the full check-in data for the latest one
            const latestCheckInData = await apiService.getWeeklyCheckIn(trainerClientData.checkins[0].id);
            setLatestCheckIn(latestCheckInData);
          }
        } catch (err) {
          console.log('No weekly check-ins found for this client');
        }
      }

      // Fetch training plans for group clients
      if (clientData.category === 'group') {
        try {
          const plans = await apiService.getClientGroupTrainingPlans(parseInt(id));
          setTrainingPlans(plans);
          setActiveTab('training-plans');
          
          // Fetch exercises for each plan
          const exercisesMap: Record<number, TrainingPlanExercise[]> = {};
          for (const plan of plans) {
            try {
              const exercises = await apiService.getTrainingPlanExercises(plan.id);
              exercisesMap[plan.id] = exercises;
            } catch (err) {
              console.log(`No exercises found for plan ${plan.id}`);
              exercisesMap[plan.id] = [];
            }
          }
          setPlanExercises(exercisesMap);
        } catch (err) {
          console.log('No training plans found for this group client');
        }
      }
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch client data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && user?.role === 'trainer' && id) {
      fetchClientData();
    }
  }, [isLoading, user, id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodEmoji = (rating: number) => {
    const emojis = ['😞', '😐', '🙂', '😊', '😄'];
    return emojis[rating - 1] || '😐';
  };

  const handleEditClient = () => {
    // TODO: Implement edit client functionality
    console.log('Edit client:', client);
  };

  const handleDeleteClient = async () => {
    if (!client) return;
    
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      try {
        await apiService.deleteClient(client.id);
        navigate('/clients');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete client');
      }
    }
  };

  const handleBackToClients = () => {
    navigate('/clients');
  };

  const getTrafficLightIcon = (rating: string) => {
    switch (rating) {
      case 'green':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'amber':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'red':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTrafficLightColor = (rating: string) => {
    switch (rating) {
      case 'green':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'amber':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'red':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getProgressColor = (progress: string) => {
    switch (progress) {
      case 'progressed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'stayed_same':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'regressed':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user?.role !== 'trainer') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">Only trainers can access client details.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Client Not Found</h1>
          <p className="text-gray-600">The client you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackToClients}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Clients</span>
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {client.user.first_name} {client.user.last_name}
              </h1>
              <p className="text-gray-600">Client Details</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleEditClient}
              className="btn-secondary flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
            <button
              onClick={handleDeleteClient}
              className="btn-danger flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Client Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Full Name</p>
              <p className="text-lg font-semibold text-gray-900">
                {client.user.first_name} {client.user.last_name}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Email</p>
              <p className="text-lg font-semibold text-gray-900">{client.user.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Member Since</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatDate(client.user.date_joined)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {client?.category === 'group' ? (
              // Group clients only see Training Plans tab
              <button
                onClick={() => setActiveTab('training-plans')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === 'training-plans'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Target className="w-4 h-4" />
                <span>Training Plans</span>
              </button>
            ) : (
              // 1-on-1 clients see standard tabs
              [
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'checkins', label: 'Check-ins', icon: Activity },
                { id: 'progress', label: 'Progress', icon: TrendingUp },
                { id: 'materials', label: 'Materials', icon: FileText }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))
            )}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Latest Check-in Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Latest Check-in</h3>
                  <button
                    onClick={() => setActiveTab('checkins')}
                    className="text-primary hover:text-primary-dark font-medium text-sm flex items-center space-x-1"
                  >
                    <span>View All Check-ins</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
                
                {latestCheckIn ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(latestCheckIn.submitted_at)}
                        </span>
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTrafficLightColor(latestCheckIn.traffic_light_rating)}`}>
                          {getTrafficLightIcon(latestCheckIn.traffic_light_rating)}
                          <span className="ml-1 capitalize">{latestCheckIn.traffic_light_rating}</span>
                        </div>
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getProgressColor(latestCheckIn.progress_assessment)}`}>
                          <span className="capitalize">{latestCheckIn.progress_assessment.replace('_', ' ')}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-sm text-gray-500">
                          Overall Grade: {latestCheckIn.overall_grade}/10
                        </div>
                        <button
                          onClick={() => navigate(`/checkins/${latestCheckIn.id}`)}
                          className="text-primary hover:text-primary-dark font-medium text-sm flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View Details</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      {latestCheckIn.weight_kg && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Weight:</span>
                          <span className="text-sm text-gray-900 ml-2">{latestCheckIn.weight_kg} kg</span>
                        </div>
                      )}
                      <div>
                        <span className="text-sm font-medium text-gray-500">Consistency:</span>
                        <span className="text-sm text-gray-900 ml-2">{latestCheckIn.average_consistency_rating}/5</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Self Confidence:</span>
                        <span className="text-sm text-gray-900 ml-2">{latestCheckIn.self_confidence_rating}/5</span>
                      </div>
                    </div>
                    
                    {latestCheckIn.biggest_win && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-500">Biggest Win:</span>
                        <p className="text-sm text-gray-900 mt-1">{latestCheckIn.biggest_win}</p>
                      </div>
                    )}
                    
                    {latestCheckIn.goals_for_next_week && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Goals for Next Week:</span>
                        <p className="text-sm text-gray-900 mt-1">{latestCheckIn.goals_for_next_week}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No check-ins yet</h3>
                    <p className="text-gray-600">This client hasn't submitted any check-ins yet.</p>
                  </div>
                )}
              </div>

              {/* Progress Summary Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Progress Summary</h3>
                  <button
                    onClick={() => setActiveTab('progress')}
                    className="text-primary hover:text-primary-dark font-medium text-sm flex items-center space-x-1"
                  >
                    <span>View Detailed Progress</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
                
                {progressData ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <Target className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">Completion Rate</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{progressData.completion_rate}%</div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Average Mood</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">{progressData.average_mood.toFixed(1)}/5</div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                        <span className="text-sm font-medium text-purple-700">Average Energy</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">{progressData.average_energy.toFixed(1)}/5</div>
                    </div>
                    
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <Activity className="w-5 h-5 text-orange-600" />
                        <span className="text-sm font-medium text-orange-700">Total Check-ins</span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">{progressData.check_ins.length}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No progress data available</h3>
                    <p className="text-gray-600">Progress data will appear once the client starts submitting check-ins.</p>
                  </div>
                )}
              </div>

              {/* Client Information Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Client Information</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Username</dt>
                      <dd className="text-sm text-gray-900">@{client.user.username}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          client.user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {client.user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Stripe Customer ID</dt>
                      <dd className="text-sm text-gray-900">
                        {client.stripe_customer_id || 'Not set'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Login</dt>
                      <dd className="text-sm text-gray-900">
                        {client.user.last_login ? formatDateTime(client.user.last_login) : 'Never'}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Activity className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Total Check-ins</span>
                      </div>
                      <span className="text-lg font-semibold text-gray-900">{checkIns.length}</span>
                    </div>
                    
                    {latestCheckIn && (
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Clock className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-gray-700">Last Check-in</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatDate(latestCheckIn.submitted_at)}
                        </span>
                      </div>
                    )}
                    
                    {progressData && (
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Target className="w-5 h-5 text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">Completion Rate</span>
                        </div>
                        <span className="text-lg font-semibold text-gray-900">
                          {progressData.completion_rate}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Check-ins Tab */}
          {activeTab === 'checkins' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Recent Check-ins</h3>
                <div className="flex items-center space-x-3">
                  {latestCheckIn && (
                    <button 
                      onClick={() => navigate(`/checkins/${latestCheckIn.id}`)}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Latest Check-in</span>
                    </button>
                  )}
                  <button className="btn-primary flex items-center space-x-2">
                    <Plus className="w-4 h-4" />
                    <span>Add Check-in</span>
                  </button>
                </div>
              </div>
              
              {checkIns.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No check-ins yet</h3>
                  <p className="text-gray-600">This client hasn't submitted any check-ins yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {checkIns.map((checkIn) => (
                    <div key={checkIn.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(checkIn.submitted_at)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Check-in #{checkIn.id}
                        </span>
                      </div>
                      <button 
                        onClick={() => navigate(`/checkins/${checkIn.id}`)}
                        className="btn-secondary flex items-center space-x-2 text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">Progress Overview</h3>
              
              {!progressData ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No progress data available</h3>
                  <p className="text-gray-600">Progress data will appear once the client starts submitting check-ins.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{progressData.completion_rate.toFixed(2)}%</div>
                      <div className="text-sm text-blue-700">Completion Rate</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{progressData.average_energy.toFixed(1)}</div>
                      <div className="text-sm text-purple-700">Average Energy</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">{progressData.check_ins.length}</div>
                      <div className="text-sm text-orange-700">Total Check-ins</div>
                    </div>
                  </div>
                  
                  {/* Weight Trend Chart */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Weight Trend</h4>
                    {progressData.weight_trend && progressData.weight_trend.length > 0 ? (
                      <div className="h-64">
                        <WeightChart weightData={progressData.weight_trend} />
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                          <p>No weight data available</p>
                          <p className="text-sm">Weight trend will appear once the client starts recording weight measurements</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Materials Tab */}
          {activeTab === 'materials' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Assigned Materials</h3>
                <button className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Assign Material</span>
                </button>
              </div>
              
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No materials assigned</h3>
                <p className="text-gray-600">This client hasn't been assigned any materials yet.</p>
              </div>
            </div>
          )}

          {/* Training Plans Tab - Only for Group Clients */}
          {activeTab === 'training-plans' && client?.category === 'group' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Training Plans</h3>
                <button
                  onClick={() => setShowTrainingPlanModal(true)}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Training Plan</span>
                </button>
              </div>
              
              {trainingPlans.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No training plans</h3>
                  <p className="text-gray-600">This group client doesn't have any training plans yet.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {trainingPlans.map((plan) => {
                    const exercises = planExercises[plan.id] || [];
                    return (
                      <div key={plan.id} className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{plan.title}</h4>
                            {plan.description && (
                              <p className="text-gray-600 mb-2">{plan.description}</p>
                            )}
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2" />
                                <span>
                                  {formatDate(plan.start_date)} - {formatDate(plan.end_date)}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <Target className="w-4 h-4 mr-2" />
                                <span>{plan.days_per_week} days per week</span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedPlanId(plan.id);
                              setShowExerciseModal(true);
                            }}
                            className="btn-primary flex items-center space-x-2 text-sm"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add Exercise</span>
                          </button>
                        </div>
                        
                        {exercises.length > 0 ? (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h5 className="text-sm font-medium text-gray-900 mb-3">Exercises</h5>
                            <div className="space-y-3">
                              {exercises.map((exercise) => (
                                <div key={exercise.id} className="bg-gray-50 rounded-lg p-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h6 className="font-medium text-gray-900">{exercise.exercise.name}</h6>
                                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
                                        {exercise.sets && <span>Sets: {exercise.sets}</span>}
                                        {exercise.reps && <span>Reps: {exercise.reps}</span>}
                                        {exercise.weight_kg && <span>Weight: {exercise.weight_kg} kg</span>}
                                        {exercise.weight_lbs && <span>Weight: {exercise.weight_lbs} lbs</span>}
                                        {exercise.distance_km && <span>Distance: {exercise.distance_km} km</span>}
                                        {exercise.distance_miles && <span>Distance: {exercise.distance_miles} miles</span>}
                                        {exercise.duration_minutes && <span>Duration: {exercise.duration_minutes} min</span>}
                                        {exercise.rest_seconds && <span>Rest: {exercise.rest_seconds}s</span>}
                                        {exercise.tempo && <span>Tempo: {exercise.tempo}</span>}
                                        {exercise.rpe && <span>RPE: {exercise.rpe}/10</span>}
                                      </div>
                                      <div className="mt-2 text-xs text-gray-500">
                                        Week {exercise.week_number}, {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][exercise.day_of_week - 1]}
                                      </div>
                                      {exercise.notes && (
                                        <p className="mt-2 text-sm text-gray-600">{exercise.notes}</p>
                                      )}
                                    </div>
                                    <button
                                      onClick={async () => {
                                        if (window.confirm('Remove this exercise from the training plan?')) {
                                          try {
                                            await apiService.deleteTrainingPlanExercise(exercise.id);
                                            const updatedExercises = exercises.filter(e => e.id !== exercise.id);
                                            setPlanExercises({ ...planExercises, [plan.id]: updatedExercises });
                                          } catch (err: any) {
                                            alert(err.response?.data?.message || 'Failed to remove exercise');
                                          }
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-800 ml-4"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 pt-4 border-t border-gray-200 text-center py-4">
                            <p className="text-sm text-gray-500">No exercises added yet. Click "Add Exercise" to get started.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Training Plan Modal */}
      {showTrainingPlanModal && client && (
        <TrainingPlanModal
          clientId={client.id}
          onClose={() => setShowTrainingPlanModal(false)}
          onSuccess={() => {
            setShowTrainingPlanModal(false);
            fetchClientData();
          }}
        />
      )}

      {/* Add Exercise Modal */}
      {showExerciseModal && selectedPlanId && (
        <AddExerciseModal
          trainingPlanId={selectedPlanId}
          onClose={() => {
            setShowExerciseModal(false);
            setSelectedPlanId(null);
          }}
          onSuccess={async () => {
            // Refresh exercises for the selected plan
            try {
              const exercises = await apiService.getTrainingPlanExercises(selectedPlanId);
              setPlanExercises({ ...planExercises, [selectedPlanId]: exercises });
            } catch (err) {
              console.error('Failed to refresh exercises');
            }
            setShowExerciseModal(false);
            setSelectedPlanId(null);
          }}
        />
      )}
    </div>
  );
};

// Training Plan Modal Component
interface TrainingPlanModalProps {
  clientId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const TrainingPlanModal: React.FC<TrainingPlanModalProps> = ({ clientId, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [daysPerWeek, setDaysPerWeek] = useState<3 | 4>(3);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Calculate end date (6 weeks from start date)
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 42); // 6 weeks = 42 days

      await apiService.createGroupTrainingPlan({
        client_id: clientId,
        title,
        description,
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0],
        days_per_week: daysPerWeek,
        workout_details: {}
      });

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create training plan');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Add Training Plan</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                rows={3}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="daysPerWeek" className="block text-sm font-medium text-gray-700 mb-1">
                Days Per Week *
              </label>
              <select
                id="daysPerWeek"
                value={daysPerWeek}
                onChange={(e) => setDaysPerWeek(parseInt(e.target.value) as 3 | 4)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                disabled={isLoading}
              >
                <option value={3}>3 days per week</option>
                <option value={4}>4 days per week</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Add Exercise Modal Component
interface AddExerciseModalProps {
  trainingPlanId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const AddExerciseModal: React.FC<AddExerciseModalProps> = ({ trainingPlanId, onClose, onSuccess }) => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(null);
  const [weekNumber, setWeekNumber] = useState<number>(1);
  const [dayOfWeek, setDayOfWeek] = useState<1 | 2 | 3 | 4 | 5 | 6 | 7>(1);
  const [order, setOrder] = useState<number>(0);
  const [sets, setSets] = useState<number | ''>('');
  const [reps, setReps] = useState<number | ''>('');
  const [weightKg, setWeightKg] = useState<number | ''>('');
  const [weightLbs, setWeightLbs] = useState<number | ''>('');
  const [distanceKm, setDistanceKm] = useState<number | ''>('');
  const [distanceMiles, setDistanceMiles] = useState<number | ''>('');
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('');
  const [restSeconds, setRestSeconds] = useState<number | ''>('');
  const [tempo, setTempo] = useState<string>('');
  const [rpe, setRpe] = useState<number | ''>('');
  const [notes, setNotes] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const data = await apiService.getExercises();
        setExercises(data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch exercises');
      }
    };
    fetchExercises();
  }, []);

  const filteredExercises = exercises.filter(exercise =>
    exercise.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exercise.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedExerciseId) {
      setError('Please select an exercise');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await apiService.createTrainingPlanExercise({
        training_plan: trainingPlanId,
        exercise_id: selectedExerciseId,
        week_number: weekNumber,
        day_of_week: dayOfWeek,
        order: order,
        sets: sets ? Number(sets) : undefined,
        reps: reps ? Number(reps) : undefined,
        weight_kg: weightKg ? Number(weightKg) : undefined,
        weight_lbs: weightLbs ? Number(weightLbs) : undefined,
        distance_km: distanceKm ? Number(distanceKm) : undefined,
        distance_miles: distanceMiles ? Number(distanceMiles) : undefined,
        duration_minutes: durationMinutes ? Number(durationMinutes) : undefined,
        rest_seconds: restSeconds ? Number(restSeconds) : undefined,
        tempo: tempo || undefined,
        rpe: rpe ? Number(rpe) : undefined,
        notes: notes || undefined,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add exercise');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Add Exercise to Training Plan</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="exercise" className="block text-sm font-medium text-gray-700 mb-2">
                Exercise *
              </label>
              <input
                type="text"
                placeholder="Search exercises..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary mb-2"
              />
              <select
                id="exercise"
                value={selectedExerciseId || ''}
                onChange={(e) => setSelectedExerciseId(Number(e.target.value) || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                disabled={isLoading}
              >
                <option value="">Select an exercise</option>
                {filteredExercises.map((exercise) => (
                  <option key={exercise.id} value={exercise.id}>
                    {exercise.name} ({exercise.category})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="weekNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Week Number *
                </label>
                <select
                  id="weekNumber"
                  value={weekNumber}
                  onChange={(e) => setWeekNumber(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  required
                  disabled={isLoading}
                >
                  {[1, 2, 3, 4, 5, 6].map(week => (
                    <option key={week} value={week}>Week {week}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="dayOfWeek" className="block text-sm font-medium text-gray-700 mb-1">
                  Day of Week *
                </label>
                <select
                  id="dayOfWeek"
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(Number(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6 | 7)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  required
                  disabled={isLoading}
                >
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={7}>Sunday</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
                Order (within workout)
              </label>
              <input
                type="number"
                id="order"
                value={order}
                onChange={(e) => setOrder(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                min="0"
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="sets" className="block text-sm font-medium text-gray-700 mb-1">
                  Sets
                </label>
                <input
                  type="number"
                  id="sets"
                  value={sets}
                  onChange={(e) => setSets(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  min="0"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="reps" className="block text-sm font-medium text-gray-700 mb-1">
                  Reps
                </label>
                <input
                  type="number"
                  id="reps"
                  value={reps}
                  onChange={(e) => setReps(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  min="0"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="weightKg" className="block text-sm font-medium text-gray-700 mb-1">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  id="weightKg"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  step="0.1"
                  min="0"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="weightLbs" className="block text-sm font-medium text-gray-700 mb-1">
                  Weight (lbs)
                </label>
                <input
                  type="number"
                  id="weightLbs"
                  value={weightLbs}
                  onChange={(e) => setWeightLbs(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  step="0.1"
                  min="0"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="distanceKm" className="block text-sm font-medium text-gray-700 mb-1">
                  Distance (km)
                </label>
                <input
                  type="number"
                  id="distanceKm"
                  value={distanceKm}
                  onChange={(e) => setDistanceKm(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  step="0.1"
                  min="0"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="distanceMiles" className="block text-sm font-medium text-gray-700 mb-1">
                  Distance (miles)
                </label>
                <input
                  type="number"
                  id="distanceMiles"
                  value={distanceMiles}
                  onChange={(e) => setDistanceMiles(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  step="0.1"
                  min="0"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="durationMinutes" className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  id="durationMinutes"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  min="0"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="restSeconds" className="block text-sm font-medium text-gray-700 mb-1">
                  Rest (seconds)
                </label>
                <input
                  type="number"
                  id="restSeconds"
                  value={restSeconds}
                  onChange={(e) => setRestSeconds(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  min="0"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="tempo" className="block text-sm font-medium text-gray-700 mb-1">
                  Tempo (e.g., 2-0-1-0)
                </label>
                <input
                  type="text"
                  id="tempo"
                  value={tempo}
                  onChange={(e) => setTempo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  placeholder="2-0-1-0"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="rpe" className="block text-sm font-medium text-gray-700 mb-1">
                  RPE (1-10)
                </label>
                <input
                  type="number"
                  id="rpe"
                  value={rpe}
                  onChange={(e) => setRpe(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  min="1"
                  max="10"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                rows={3}
                disabled={isLoading}
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Exercise'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientDetails; 